/**
 * Provider List Page - Table view of all telephony providers.
 */
import { useEffect, useState, useCallback, useMemo, type ChangeEvent, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Hub,
  Cloud,
  Computer,
  Phone,
  Clear,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { RequirePermission } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import type { AIAgentProvider } from '../types';
import usePageTitle from '../hooks/usePageTitle';

const providerTypeConfig: Record<
  string,
  {
    color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
    icon?: ReactElement;
  }
> = {
  cloud: { color: 'primary', icon: <Cloud sx={{ fontSize: 16 }} /> },
  local: { color: 'secondary', icon: <Computer sx={{ fontSize: 16 }} /> },
  asterisk: { color: 'warning', icon: <Phone sx={{ fontSize: 16 }} /> },
  sip: { color: 'default', icon: <Phone sx={{ fontSize: 16 }} /> },
};

export default function ProviderListPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:providers.title'));
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const [providers, setProviders] = useState<AIAgentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalProviders, setTotalProviders] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const mobileCardShadow = isDarkMode ? '0 20px 35px rgba(0,0,0,0.65)' : '0 15px 30px rgba(0,0,0,0.08)';
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  const { confirm, DialogComponent } = useConfirmDialog();

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['scope', 'is_active', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const scopeFilterValue = filters.scope ?? '';
  const statusFilterValue = filters.is_active ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || '-created_at';

  const scopeOptions = useMemo(
    () => [
      { value: 'global', label: t('pages:providers.scope.global') },
      { value: 'tenant', label: t('pages:providers.scope.tenant') },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'true', label: t('common:status.active') },
      { value: 'false', label: t('common:status.inactive') },
    ],
    [t],
  );

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (scopeFilterValue) {
        params.scope = scopeFilterValue;
      }

      if (statusFilterValue) {
        params.is_active = statusFilterValue === 'true';
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/ai-agents-providers/', {
        params,
      });

      const data = response.data.results ?? response.data;
      setProviders(Array.isArray(data) ? data : []);
      setTotalProviders(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading providers:', err);
      setError(error?.response?.data?.detail || t('pages:providers.errors.loading'));
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, scopeFilterValue, searchFilterValue, statusFilterValue, t]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleDelete = async (provider: AIAgentProvider) => {
    const confirmed = await confirm({
      title: t('pages:providers.deleteConfirm.title'),
      message: t('pages:providers.deleteConfirm.message', { name: provider.name }),
      type: 'warning',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('common:actions.delete'),
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/ai-agents-providers/${provider.id}/`);
      fetchProviders();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error?.response?.data?.error || t('pages:providers.errors.deleteFailed'));
    }
  };

  const headerActions = (
    <>
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={fetchProviders}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="providers_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/providers/new"
        >
          {t('pages:providers.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:providers.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
      />
      <FormControl size="small" sx={{ minWidth: 160, ...filterInputSx }}>
        <InputLabel id="providers-scope-filter-label">{t('pages:providers.filters.scopeLabel')}</InputLabel>
        <Select
          labelId="providers-scope-filter-label"
          value={scopeFilterValue}
          label={t('pages:providers.filters.scopeLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ scope: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {scopeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
        <InputLabel id="providers-status-filter-label">{t('pages:providers.filters.statusLabel')}</InputLabel>
        <Select
          labelId="providers-status-filter-label"
          value={statusFilterValue}
          label={t('pages:providers.filters.statusLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ is_active: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Clear />}
        disabled={!searchFilterValue && !scopeFilterValue && !statusFilterValue}
        onClick={() => {
          if (!searchFilterValue && !scopeFilterValue && !statusFilterValue) return;
          setParams({ search: null, scope: null, is_active: null }, { resetPage: true });
        }}
        sx={{
          ...filterInputSx,
          textTransform: 'none',
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const table = isMobile ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      {loading ? (
        [...Array(3)].map((_, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              boxShadow: mobileCardShadow,
            }}
          >
            <Box sx={{ p: 2 }}>
              <Skeleton height={28} width="60%" />
              <Skeleton height={18} sx={{ mt: 1, width: '40%' }} />
              <Skeleton height={32} sx={{ mt: 2 }} />
            </Box>
          </Card>
        ))
      ) : providers.length === 0 ? (
        <Card
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: 'none',
          }}
        >
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <PsychologyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('pages:providers.noProviders')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              component={RouterLink}
              to="/providers/new"
              sx={{ ...filterInputSx, textTransform: 'none' }}
            >
              {t('pages:providers.createFirst')}
            </Button>
          </Box>
        </Card>
      ) : (
        providers.map((provider) => {
          const typeConfig = providerTypeConfig[provider.providerType] ?? { color: 'default' };
          return (
            <Card
              key={provider.id}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                boxShadow: mobileCardShadow,
              }}
            >
              <Box sx={{ px: 2, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {provider.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {provider.providerType}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    icon={typeConfig.icon}
                    label={provider.providerType}
                    color={typeConfig.color}
                    size="small"
                    variant="outlined"
                  />
                  <Chip label={provider.scope === 'global' ? t('pages:providers.scope.global') : t('pages:providers.scope.tenant')} size="small" variant="outlined" />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              >
                <Chip
                  label={provider.isActive ? t('common:status.active') : t('common:status.inactive')}
                  size="small"
                  color={provider.isActive ? 'success' : 'default'}
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <RequirePermission permission="providers_update">
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/providers/${provider.id}`}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('common:actions.edit')}
                    </Button>
                  </RequirePermission>
                  <RequirePermission permission="providers_delete">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(provider)}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('common:actions.delete')}
                    </Button>
                  </RequirePermission>
                </Box>
              </Box>
            </Card>
          );
        })
      )}
    </Box>
  ) : (
    <Table>
      <TableHead>
        <TableRow sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}>
          <TableCell>{columnTitle(t('pages:providers.name'))}</TableCell>
          <TableCell>{columnTitle(t('pages:providers.type'))}</TableCell>
          <TableCell>{columnTitle(t('pages:providers.filters.scopeLabel'))}</TableCell>
          <TableCell align="center">{columnTitle(t('pages:providers.aiAgentEndpoints'))}</TableCell>
          <TableCell align="center">{columnTitle(t('pages:providers.status'))}</TableCell>
          <TableCell align="center">{columnTitle(t('common:actions.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {[...Array(6)].map((_, j) => (
                <TableCell key={j}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : providers.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
              <Hub sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:providers.noProviders')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                component={RouterLink}
                to="/providers/new"
                sx={{ textTransform: 'none' }}
              >
                {t('pages:providers.createFirst')}
              </Button>
            </TableCell>
          </TableRow>
        ) : (
          providers.map((provider) => {
            const typeConfig = providerTypeConfig[provider.providerType] ?? { color: 'default' };

            return (
              <TableRow key={provider.id} hover>
                <LinkTableCell to={`/providers/${provider.id}`}>
                  <Typography fontWeight={600}>{provider.name}</Typography>
                </LinkTableCell>
                <TableCell>
                  <Chip
                    icon={typeConfig.icon}
                    label={provider.providerType}
                    color={typeConfig.color}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={provider.scope === 'global' ? t('pages:providers.scope.global') : t('pages:providers.scope.tenant')}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{provider.aiAgentEndpointsCount}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={provider.isActive ? t('common:status.active') : t('common:status.inactive')}
                    color={provider.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <RequirePermission permission="providers_update">
                    <Tooltip title={t('common:actions.edit')}>
                      <IconButton size="small" component={RouterLink} to={`/providers/${provider.id}`}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </RequirePermission>
                  <RequirePermission permission="providers_delete">
                    <Tooltip title={t('common:actions.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(provider)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </RequirePermission>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const tableFooter =
    totalProviders > 0 ? (
      <LinkPagination
        component="div"
        count={totalProviders}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        getItemAriaLabel={(type) => {
          if (type === 'first') return t('common:pagination.firstPage');
          if (type === 'last') return t('common:pagination.lastPage');
          if (type === 'next') return t('common:pagination.nextPage');
          return t('common:pagination.previousPage');
        }}
        getPageLink={getPageLink}
      />
    ) : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <Hub sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:providers.title'),
          subtitle: t('pages:providers.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
        tableContainerSx={isMobile ? { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } : undefined}
        errorContent={
          error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'error.main', borderRadius: 1 }}>
              <Typography color="error.contrastText">{error}</Typography>
            </Box>
          )
        }
      />

      {/* Confirm Dialog */}
      {DialogComponent}
    </Box>
  );
}
